import React from 'react';
import Images from 'assets/images/imageData';
import './ActivityTimeline.css';

const ActivityTimeline = ({ data = [] }) => {
  const formatDate = (date) => {
    return new Date(date).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const sortedHistory = [...data].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  if (sortedHistory.length === 0) return <p className="text-muted p-3">No activity yet.</p>;

  return (
    <div className="pr-timeline-simple">
      {sortedHistory.map((item, idx) => {
        const isLast = idx === sortedHistory.length - 1;
        const dateTimeStr = formatDate(new Date(item.created_at));
        const statusId = item.status_id;
        const dotColor = statusId === 3 ? '#2f9e44' : statusId === 4 ? '#e03131' : statusId === 8 ? '#868e96' : '#6030be';

        // Resilient parsing to handle objects, arrays, and JSON strings (found in PR history)
        const parseWrapper = (input) => {
          if (!input) return {};
          if (typeof input === 'object') return input;
          if (typeof input === 'string') {
            try { return JSON.parse(input); } catch (e) { return {}; }
          }
          return {};
        };

        const currentObj = parseWrapper(item.current_items);
        const requestedObj = parseWrapper(item.requested_items);

        const parseItems = (obj) => {
          if (!obj) return [];
          if (Array.isArray(obj)) return obj;
          if (obj.items) {
            if (Array.isArray(obj.items)) return obj.items;
            if (typeof obj.items === 'object') {
              const vals = Object.values(obj.items);
              return vals.flat();
            }
          }
          return [];
        };

        const getItemKey = (i) => i.material_service_id || i.item_id || i.id || i.material_service_code;

        const aggregateItems = (items) => {
          const map = {};
          items.forEach(item => {
            const key = getItemKey(item);
            if (!key) return;
            if (!map[key]) {
              map[key] = { ...item, quantity: 0 };
            }
            map[key].quantity = (map[key].quantity || 0) + (Number(item.quantity) || 0);
          });
          return Object.values(map);
        };

        const prevItems = aggregateItems(parseItems(currentObj));
        const reqItems = aggregateItems(parseItems(requestedObj));
        
        const allKeys = Array.from(new Set([...prevItems.map(getItemKey), ...reqItems.map(getItemKey)]));
        const changedItems = allKeys.map(key => {
          const prev = prevItems.find(i => getItemKey(i) === key);
          const reqItem = reqItems.find(i => getItemKey(i) === key);
          
          const prevQty = Number(prev?.quantity || 0);
          const reqQty = Number(reqItem?.quantity || 0);
          
          if (prevQty === reqQty) return null;
          
          return { 
            id: key, 
            itemName: reqItem?.name || prev?.name || 'Unknown Item', 
            prevQty, 
            reqQty 
          };
        }).filter(Boolean);

        const currentTotal = currentObj?.total || currentObj?.pr_total || currentObj?.total_amount;
        const currentSub = currentObj?.sub_total || currentObj?.pr_sub_total || currentObj?.subtotal_amount;
        const requestedTotal = requestedObj?.total || requestedObj?.pr_total || requestedObj?.total_amount;
        const requestedSub = requestedObj?.sub_total || requestedObj?.pr_sub_total || requestedObj?.subtotal_amount;


        return (
          <div key={`h-${idx}`} className={`timeline-item-simple ${isLast ? 'last' : ''}`}>
            <div className="timeline-dot-simple" style={{ color: dotColor }} />
            <div className="timeline-card-simple">
              <div className="card-header-simple">
                <span className="badge-simple history">{item.status}</span>
                <span className="time-simple">{dateTimeStr}</span>
              </div>
              <div className="card-body-simple">
                <div className="activity-details-simple">
                  <div className="detail-row">
                    <span className="detail-label">Actioned By:</span>
                    <span className="detail-value">{item.created_by || 'N/A'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Remark:</span>
                    <span className="detail-value">{item.remark || 'N/A'}</span>
                  </div>
                  {item.next_status && (
                    <div className="detail-row">
                      <span className="detail-label">Next Step:</span>
                      <span className="detail-value highlighted">{item.next_status}</span>
                    </div>
                  )}
                </div>

                {changedItems.length > 0 && (
                  <div className="edit-inline-section">
                    <div className="edit-inline-title">Items Modified</div>
                    <div className="diff-table-container-simple">
                      <table className="diff-table-simple">
                        <thead>
                          <tr>
                            <th className="item-name-col">Item</th>
                            <th className="qty-col">Previous Qty</th>
                            <th className="qty-col">Updated Qty</th>
                          </tr>
                        </thead>
                        <tbody>
                          {changedItems.map(ci => (
                            <tr key={ci.id}>
                              <td>{ci.itemName}</td>
                              <td className="text-center font-weight-bold text-danger">{ci.prevQty}</td>
                              <td className="text-center font-weight-bold text-success">{ci.reqQty}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {requestedSub !== undefined && (
                      <div className="edit-totals-summary">
                        <div className="total-box prev">
                          <div className="total-label">Previous Totals</div>
                          <div className="total-val">Sub: {currentSub ?? '0'} | Total: {currentTotal ?? '0'}</div>
                        </div>
                        <div className="total-box req">
                          <div className="total-label">Updated Totals</div>
                          <div className="total-val">Sub: {requestedSub ?? '0'} | Total: {requestedTotal ?? '0'}</div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {item.document && (
                  <div className="document-preview-container">
                    <div className="detail-label mb-2">Attached Document:</div>
                    <a href={item.document} target="_blank" rel="noopener noreferrer" className="doc-preview-link">
                      {item.document.match(/\.(jpeg|jpg|gif|png)$/i) ? (
                        <div className="img-preview-box">
                          <img src={item.document} alt="preview" />
                          <div className="preview-overlay">Click to View</div>
                        </div>
                      ) : (
                        <div className="file-preview-box">
                          <img src={Images?.viewIcon} alt="file" />
                          <span>View Document</span>
                        </div>
                      )}
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ActivityTimeline;
